import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import * as api from 'constants/api';
import { calculateChange } from './change_calculations';
import { calculateSummary } from './summary_calculations';
import { calculateSummary as calculateManagerReport } from './manager_report';
import { calculateSummary as calculateManagerReportV2 } from './manager_v2';
import { calculateSummary as calculateManagerReportV3 } from './manager_v3';
import { calculateOrganizationReport } from './organization_report';
import { calculateOrganizationReportManager } from './organization_report_manager';
import { calculateOrganizationReportWithDemographics } from './organization_report_demo';
import {
    SG_GET_CULTURE_AUDIT_REPORTS,
    SG_GET_ORGANIZATION_GROUP,
    LOAD_EMPLOYEES,
    SG_FETCH_ORGANIZATION_GROUP,
    SET_RAW_DATA,
    SET_DATA_DATES,
    SET_NAVIGATION,
    SET_FILTERED_DATA,
    SET_FILTERED_LAST_DATA,
} from 'constants/actions';
import { SURVEY_TYPE } from 'constants/surveys';
import {
    filter_data,
    get_dates,
    get_most_recent,
    build_default_State,
} from 'reports/Audit/data_navigation/calculate_data';
import {
    checkPersonality,
    convert_personality_to_categories,
} from 'app/audit/calculations';
import {
    get_last_responses_group,
    sort_historical_data_group,
} from '../NewAnalytics/get_last_and_historical_cohort';

const safeDateFrom = (value) => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const formatShortDate = (value) => {
    const d = safeDateFrom(value);
    if (!d) return 'N/A';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const pickFirstTruthy = (...candidates) => {
    for (const c of candidates) {
        if (c === 0) return c;
        if (c) return c;
    }
    return null;
};

const pickGroupNameFromReport = (r) => {
    if (!r) return null;
    return (
        r.organization_group_name ||
        r.group_name ||
        r.team_name ||
        (typeof r.organization_group === 'object' ? r.organization_group?.name : null) ||
        (typeof r.group === 'object' ? r.group?.name : null) ||
        (typeof r.team === 'object' ? r.team?.name : null) ||
        null
    );
};

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Raleway', sans-serif;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #2A3039;
  margin: 0 0 8px 0;
  font-family: 'Raleway', sans-serif;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666D79;
  margin: 0;
  font-weight: 400;
`;

const ControlsSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #2A3039;
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #DFDFDF;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  background: white;
  color: #2A3039;
  min-width: 200px;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #27CDA7;
  }

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }

  &:disabled {
    background: #F2F2F2;
    cursor: not-allowed;
    color: #B3B3B3;
  }
`;

const ManagerSearchRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ManagerSearchContainer = styled.div`
  position: relative;
  min-width: 260px;
`;

const ManagerSearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #DFDFDF;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  background: white;
  color: #2A3039;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #27CDA7;
  }

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }

  &:disabled {
    background: #F2F2F2;
    cursor: not-allowed;
    color: #B3B3B3;
  }
`;

const ManagerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #DFDFDF;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  z-index: 50;
  overflow: hidden;
  max-height: 260px;
  overflow-y: auto;
`;

const ManagerDropdownItem = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #F0F0F0;
  transition: background-color 0.15s ease;

  &:hover {
    background: #F8FAFF;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ManagerDropdownName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #2A3039;
`;

const ManagerDropdownEmail = styled.div`
  font-size: 12px;
  color: #666D79;
  margin-top: 2px;
`;

const ManagerNoResults = styled.div`
  padding: 12px;
  font-size: 13px;
  color: #666D79;
`;

const ManagerClearButton = styled.button`
  padding: 10px 14px;
  background: white;
  color: #2D70E2;
  border: 2px solid #2D70E2;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2D70E2;
    color: white;
  }
`;

const ManagerInlineNote = styled.div`
  font-size: 12px;
  color: #666D79;
`;

const ManagerInlineError = styled.div`
  font-size: 12px;
  color: #F15062;
`;

const TriggerButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #27CDA7 0%, #2D70E2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 44px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(45, 112, 226, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #B3B3B3;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ResultsSection = styled.div`
  margin-top: 24px;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ResultsTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #2A3039;
  margin: 0;
`;

const CopyButton = styled.button`
  padding: 8px 16px;
  background: white;
  color: #2D70E2;
  border: 2px solid #2D70E2;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2D70E2;
    color: white;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const JsonContainer = styled.div`
  background: #F8FAFF;
  border: 1px solid #DFDFDF;
  border-radius: 8px;
  padding: 20px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  color: #2A3039;
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid #DFDFDF;
    border-top: 3px solid #27CDA7;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #FEF3F4;
  border: 1px solid #F15062;
  border-radius: 8px;
  padding: 16px;
  color: #F15062;
  font-size: 14px;
  margin-top: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666D79;
  font-size: 14px;
`;

const StatusText = styled.p`
  font-size: 12px;
  color: #666D79;
  margin-top: 8px;
  font-style: italic;
`;

const TokenUsageDisplay = styled.div`
  background: #F0F7FF;
  border: 2px solid #2D70E2;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  color: #2A3039;
`;

const TokenUsageTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #2D70E2;
  font-family: 'Raleway', sans-serif;
`;

const TokenRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 8px;
    border-top: 1px solid #DFDFDF;
    font-weight: 600;
  }
`;

const TokenLabel = styled.span`
  color: #666D79;
`;

const TokenValue = styled.span`
  color: #2A3039;
  font-weight: 600;
`;

const CostValue = styled.span`
  color: #27CDA7;
  font-weight: 600;
  font-size: 13px;
`;

const PricingNote = styled.div`
  font-size: 10px;
  color: #666D79;
  margin-top: 8px;
  font-style: italic;
  border-top: 1px solid #DFDFDF;
  padding-top: 8px;
`;

const ReportV2 = () => {
    const dispatch = useDispatch();
    const [selectedCalculation, setSelectedCalculation] = useState('changeAnalysis');
    const [selectedAnchorParent, setSelectedAnchorParent] = useState('');
    const [selectedDebriefId, setSelectedDebriefId] = useState(''); // '' = Latest (default)
    const [selectedModel, setSelectedModel] = useState('gpt-4.1');
    const [selectedAnchors, setSelectedAnchors] = useState([]);
    const [combineAnchors, setCombineAnchors] = useState(false);
    const [selectedManagerId, setSelectedManagerId] = useState('');
    const [allPermissionGroups, setAllPermissionGroups] = useState([]);
    const [allGroupsLoading, setAllGroupsLoading] = useState(false);
    const [allGroupsError, setAllGroupsError] = useState(null);
    const [managerSearchTerm, setManagerSearchTerm] = useState('');
    const [showManagerDropdown, setShowManagerDropdown] = useState(false);
    const [calculationResult, setCalculationResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [tokenUsage, setTokenUsage] = useState({
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
        call_count: 0,
        input_cost: 0,
        output_cost: 0,
        total_cost: 0
    });

    const core_data = useSelector(
        (state) => state.audit?.core_data,
        shallowEqual
    );

    const surveyStructure = useSelector(
        (state) => state.surveystructure?.survey_structure,
        shallowEqual
    );

    console.log("core_data", core_data);
    const raw_data = useSelector((state) => state.audit?.raw_data, shallowEqual);
    const organizations = useSelector((state) => state.organizations, shallowEqual);
    const auditDates = useSelector((state) => state.audit?.dates, shallowEqual);
    const auditNav = useSelector((state) => state.audit?.nav, shallowEqual);
    const auditFilteredData = useSelector((state) => state.audit?.filtered_data, shallowEqual);
    const auditFilteredLastData = useSelector((state) => state.audit?.filtered_last_data, shallowEqual);
    const debriefSchedules = useSelector((state) => state.debrief_schedule, shallowEqual);
    const { organization_groups, groupsPending, employees, organization_group_detail } = useSelector(
        (state) => ({
            organization_groups: Array.isArray(state.organization_group?.organization_groups)
                ? state.organization_group.organization_groups
                : [],
            groupsPending: state.organization_group?.pending,
            employees: Array.isArray(state.employees?.employees) ? state.employees.employees : [],
            organization_group_detail: state.organization_group?.organization_group,
        }),
        shallowEqual
    );

    // Organization + culture audit reports state for triggering fetch on refresh
    const {
        organizationId,
        cultureAuditReports,
        reportsPending,
    } = useSelector(
        (state) => ({
            organizationId:
                state.selectedOrg?.organization?.id ||
                Number(state.auth?.organization_id),
            cultureAuditReports: state.debrief_schedule?.culture_audit_reports,
            reportsPending: state.debrief_schedule?.reportsPending,
        }),
        shallowEqual
    );

    // Track if we've already requested culture audit reports once for this component mount.
    // This prevents repeatedly re-dispatching on persistent failures (e.g. CORS errors),
    // which would otherwise hammer the `/report/culture-audit` endpoint.
    const hasRequestedCultureAuditRef = useRef(false);
    const allGroupsRequestedForOrgRef = useRef(null);

    // Fetch organization groups (permission groups) for manager selection
    useEffect(() => {
        const orgId = Number(organizationId);
        if (!orgId || Number.isNaN(orgId) || orgId <= 0) return;
        dispatch({
            type: SG_GET_ORGANIZATION_GROUP,
            payload: {
                page: 1,
                page_size: 200,
                organization: orgId,
                group_type: 'permission',
            },
        });
    }, [dispatch, organizationId]);

    // Fetch ALL permission groups across pagination for a complete manager list (not limited to one page in redux)
    useEffect(() => {
        const orgId = Number(organizationId);
        if (!orgId || Number.isNaN(orgId) || orgId <= 0) return;

        // Avoid re-fetching endlessly for the same orgId unless it changes
        if (allGroupsRequestedForOrgRef.current === orgId) return;
        allGroupsRequestedForOrgRef.current = orgId;

        let cancelled = false;

        const nextToQueryString = (nextUrl) => {
            if (!nextUrl) return null;
            const str = String(nextUrl);
            const qIndex = str.indexOf('?');
            if (qIndex >= 0) return str.slice(qIndex + 1);
            // If backend returns a relative URL without '?', we can't safely proceed.
            return null;
        };

        const run = async () => {
            setAllGroupsLoading(true);
            setAllGroupsError(null);
            try {
                const mapById = new Map();
                let queryOrParams = {
                    page: 1,
                    page_size: 500,
                    organization: orgId,
                    group_type: 'permission',
                };

                // Hard safety cap to avoid infinite loops on malformed `next`
                for (let i = 0; i < 60; i++) {
                    if (cancelled) return;

                    const res = await api.GET_ORGANIZATION_GROUP(queryOrParams);
                    const data = res?.data;
                    const results = Array.isArray(data?.results)
                        ? data.results
                        : Array.isArray(data)
                        ? data
                        : [];

                    for (const g of results) {
                        if (g?.id == null) continue;
                        mapById.set(g.id, g);
                    }

                    const nextUrl = data?.links?.next || data?.next || null;
                    const nextQuery = nextToQueryString(nextUrl);
                    if (!nextQuery) break;
                    queryOrParams = nextQuery;
                }

                if (!cancelled) {
                    setAllPermissionGroups(Array.from(mapById.values()));
                }
            } catch (e) {
                if (!cancelled) {
                    console.error('[ReportV2] Failed to fetch all permission groups:', e);
                    setAllPermissionGroups([]);
                    setAllGroupsError(e?.message || 'Failed to load managers');
                }
            } finally {
                if (!cancelled) setAllGroupsLoading(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [organizationId]);

    // Ensure employees are available for manager names
    useEffect(() => {
        if (!employees || employees.length === 0) {
            dispatch({ type: LOAD_EMPLOYEES });
        }
    }, [dispatch, employees]);

    // Ensure culture audit reports are loaded when landing directly on this page (e.g. hard refresh)
    useEffect(() => {
        const orgId = Number(organizationId);

        if (!orgId || Number.isNaN(orgId) || orgId <= 0) {
            return;
        }

        const hasReports =
            Array.isArray(cultureAuditReports?.response) &&
            cultureAuditReports.response.length > 0;

        if (hasReports || reportsPending || hasRequestedCultureAuditRef.current) {
            return;
        }

        hasRequestedCultureAuditRef.current = true;

        dispatch({
            type: SG_GET_CULTURE_AUDIT_REPORTS,
            payload: {
                auditTypeId: SURVEY_TYPE.AUDIT,
                organization: orgId,
            },
        });
    }, [dispatch, organizationId, cultureAuditReports, reportsPending]);

    console.log("raw_data", raw_data);
    // Get anchor parents from core_data
    const anchorParents = core_data?.categories?.[0]?.categories || [];

    const resetLocalOutputs = () => {
        setCalculationResult(null);
        setError(null);
        setCopySuccess(false);
        setTokenUsage({
            prompt_tokens: 0,
            completion_tokens: 0,
            total_tokens: 0,
            call_count: 0,
            input_cost: 0,
            output_cost: 0,
            total_cost: 0,
        });
    };

    const debriefOptions = useMemo(() => {
        const list = Array.isArray(cultureAuditReports?.response)
            ? cultureAuditReports.response
            : [];

        const sorted = [...list].sort((a, b) => {
            const ad = safeDateFrom(a?.close_at || a?.close_date || a?.date);
            const bd = safeDateFrom(b?.close_at || b?.close_date || b?.date);
            if (ad && bd) return bd.getTime() - ad.getTime();
            if (ad && !bd) return -1;
            if (!ad && bd) return 1;
            return Number(b?.id || 0) - Number(a?.id || 0);
        });

        return sorted.map((r) => {
            const id = String(r?.id);
            const surveyName =
                r?.name ||
                r?.question?.name ||
                r?.question?.title ||
                `Debrief ${id}`;
            const dateLabel = formatShortDate(r?.close_at || r?.close_date || r?.date);
            const groupLabel = pickGroupNameFromReport(r);
            const label = [surveyName, dateLabel, groupLabel].filter(Boolean).join(' • ');
            return { value: id, label, report: r };
        });
    }, [cultureAuditReports?.response]);

    const baselineAuditStateRef = useRef(null);

    const storeBaselineAuditStateIfNeeded = () => {
        if (baselineAuditStateRef.current) return;
        baselineAuditStateRef.current = {
            raw_data,
            dates: auditDates,
            nav: auditNav,
            filtered_data: auditFilteredData,
            filtered_last_data: auditFilteredLastData,
        };
    };

    const restoreBaselineAuditStateIfAvailable = () => {
        const snap = baselineAuditStateRef.current;
        if (!snap) return;
        dispatch({ type: SET_RAW_DATA, payload: snap.raw_data || [] });
        dispatch({ type: SET_DATA_DATES, payload: snap.dates || [] });
        dispatch({ type: SET_NAVIGATION, payload: snap.nav || {} });
        dispatch({ type: SET_FILTERED_DATA, payload: snap.filtered_data || [] });
        dispatch({ type: SET_FILTERED_LAST_DATA, payload: snap.filtered_last_data || [] });
    };

    const loadSelectedDebriefIntoRedux = (selectedReport) => {
        if (!selectedReport) return;

        const allReportsRaw = Array.isArray(cultureAuditReports?.response)
            ? cultureAuditReports.response
            : [];

        // Optional personality conversion to match standard loader behavior
        const shouldConvertPersonality = checkPersonality(organizations);
        const allReportsDesc = shouldConvertPersonality
            ? convert_personality_to_categories([...allReportsRaw]).sort((a, b) => (b?.id || 0) - (a?.id || 0))
            : [...allReportsRaw].sort((a, b) => (b?.id || 0) - (a?.id || 0));

        let selectedReports = [selectedReport];
        if (shouldConvertPersonality) {
            selectedReports = convert_personality_to_categories(selectedReports);
        }

        const sortedSelectedDesc = [...selectedReports].sort((a, b) => (b?.id || 0) - (a?.id || 0));
        const most_recent = get_most_recent(sortedSelectedDesc);

        const selectedIdsArray = sortedSelectedDesc
            .map((r) => r?.id)
            .filter((id) => id != null);

        // Group method for last + historical (mirrors NewAnalytics default)
        most_recent.last = get_last_responses_group(allReportsDesc, selectedIdsArray);
        const historical_data = sort_historical_data_group(allReportsDesc, selectedIdsArray);

        const processedData = [most_recent, ...(historical_data || [])];

        const groupTitle = pickGroupNameFromReport(selectedReport);
        const surveyName =
            selectedReport?.name ||
            selectedReport?.question?.name ||
            selectedReport?.question?.title ||
            `Debrief ${selectedReport?.id}`;

        processedData[0].datasetMetadata = {
            timestamp: Date.now(),
            source: `reportv2-debrief-${selectedReport?.id}`,
            selectionType: 'debrief',
            calculationMethod: 'group',
            surveyName,
            groupTitle,
            selectedEventIds: [selectedReport?.id],
            debriefScheduleId: pickFirstTruthy(
                selectedReport?.debrief_schedule_id,
                selectedReport?.debrief_schedule,
                selectedReport?.schedule_id,
                selectedReport?.schedule,
                selectedReport?.survey_schedule_id,
                selectedReport?.survey_schedule,
                selectedReport?.survey_event_id,
                selectedReport?.survey_event
            ) || undefined,
            organizationGroupId: pickFirstTruthy(
                selectedReport?.organization_group_id,
                typeof selectedReport?.organization_group === 'object'
                    ? selectedReport?.organization_group?.id
                    : selectedReport?.organization_group,
                selectedReport?.group_id,
                typeof selectedReport?.group === 'object' ? selectedReport?.group?.id : selectedReport?.group,
                selectedReport?.team_id,
                typeof selectedReport?.team === 'object' ? selectedReport?.team?.id : selectedReport?.team
            ) || undefined,
            organizationGroupName: groupTitle || undefined,
            seriesSelected: true,
        };

        dispatch({ type: SET_RAW_DATA, payload: processedData });
        dispatch({ type: SET_DATA_DATES, payload: get_dates(historical_data || []) });

        if (surveyStructure?.[0]) {
            const defState = build_default_State(surveyStructure[0]);
            dispatch({ type: SET_NAVIGATION, payload: defState });

            const filtered = filter_data(processedData, defState, surveyStructure[0]);
            dispatch({ type: SET_FILTERED_DATA, payload: filtered?.filter_data || [] });
            dispatch({ type: SET_FILTERED_LAST_DATA, payload: filtered?.filter_data_last || [] });
        }
    };

    // When the debrief dropdown changes, optionally hydrate Redux with that selection.
    useEffect(() => {
        // Latest (default): restore baseline if we previously overwrote data.
        if (!selectedDebriefId) {
            restoreBaselineAuditStateIfAvailable();
            resetLocalOutputs();
            return;
        }

        const match = debriefOptions.find((o) => String(o.value) === String(selectedDebriefId));
        if (!match?.report) return;

        storeBaselineAuditStateIfNeeded();
        loadSelectedDebriefIntoRedux(match.report);
        resetLocalOutputs();
    }, [selectedDebriefId, debriefOptions]);

    // Model options with pricing
    const modelOptions = [
        {
            value: 'gpt-4.1',
            label: 'GPT-4.1',
            inputCostPerMillion: 2.00,
            outputCostPerMillion: 8.00
        },
        {
            value: 'gpt-4.1-mini',
            label: 'GPT-4.1 Mini',
            inputCostPerMillion: 0.40,
            outputCostPerMillion: 1.60
        },
        {
            value: 'gpt-4.1-nano',
            label: 'GPT-4.1 Nano',
            inputCostPerMillion: 0.10,
            outputCostPerMillion: 0.40
        },
        {
          value: 'gpt-5-nano',
          label: 'GPT-5 Nano',
          inputCostPerMillion: 0.05,
          outputCostPerMillion: 0.40
      },
      {
          value: 'gpt-5-mini',
          label: 'GPT-5 Mini',
          inputCostPerMillion: 0.25,
          outputCostPerMillion: 2.00
      },
        {
            value: 'grok-4-latest',
            label: 'Grok 4',
            inputCostPerMillion: 3.00,
            outputCostPerMillion: 15.00
        }
    ];

    // Get current model pricing
    const currentModel = modelOptions.find(model => model.value === selectedModel);

    // Set default anchor parent if not selected and data is available
    useEffect(() => {
        if (anchorParents.length > 0 && !selectedAnchorParent) {
            setSelectedAnchorParent(anchorParents[0].id);
        }
    }, [anchorParents, selectedAnchorParent]);

    // Reset selected anchors when parent changes
    useEffect(() => {
        setSelectedAnchors([]);
    }, [selectedAnchorParent]);

    const calculationOptions = [
        { value: 'changeAnalysis', label: 'Change Analysis', func: calculateChange },
        { value: 'summaryAnalysis', label: 'Summary Analysis', func: calculateSummary },
        { value: 'managerReport', label: 'Manager Report', func: calculateManagerReport },
        { value: 'managerReportV2', label: 'Manager Report V2', func: calculateManagerReportV2 },
        { value: 'managerReportV3', label: 'Manager Report V3', func: calculateManagerReportV3 },
        { value: 'organizationReport', label: 'Organization Report', func: calculateOrganizationReport },
        { value: 'organizationReportManager', label: 'Organization Report (Manager edition)', func: calculateOrganizationReportManager },
        {
            value: 'organizationReportWithDemographics',
            label: 'Organization Report (with demographics)',
            func: (raw, core, deb, anchorParent, tokenCb, model, selectedAnchorsArg, combineAnchorsArg) =>
                calculateOrganizationReportWithDemographics(
                    raw,
                    core,
                    deb,
                    anchorParent,
                    tokenCb,
                    model,
                    selectedAnchorsArg,
                    combineAnchorsArg,
                    surveyStructure
                )
        }
    ];

    const canCalculate = raw_data && core_data && selectedAnchorParent;

    const formatManagerName = (managerObj, fallbackId) => {
        if (managerObj && typeof managerObj === 'object') {
            const fullName = `${managerObj.first_name || ''} ${managerObj.last_name || ''}`.trim();
            if (fullName) return fullName;
            if (managerObj.email) return managerObj.email;
        }
        const matchEmp = employees?.find((e) => String(e.id) === String(fallbackId));
        if (matchEmp) {
            const fullName = `${matchEmp.first_name || ''} ${matchEmp.last_name || ''}`.trim();
            return fullName || matchEmp.email || `Manager ${fallbackId}`;
        }
        return `Manager ${fallbackId}`;
    };

    const permissionGroupsForManagers = useMemo(() => {
        return (Array.isArray(allPermissionGroups) && allPermissionGroups.length > 0)
            ? allPermissionGroups
            : (Array.isArray(organization_groups) ? organization_groups : []);
    }, [allPermissionGroups, organization_groups]);

    const managerOptions = useMemo(() => {
        // Deduplicate by manager id, preferring the group with the largest membersCount
        const bestByManagerId = new Map();

        for (const group of permissionGroupsForManagers) {
            const managerId =
                typeof group?.manager === 'object' ? group.manager?.id : group?.manager;
            if (!managerId) continue;

            const managerObj =
                typeof group.manager === 'object'
                    ? group.manager
                    : employees?.find((e) => String(e.id) === String(managerId));

            const label = formatManagerName(managerObj, managerId);
            const email = managerObj?.email || null;
            const membersCount = Array.isArray(group?.members) ? group.members.length : 0;

            const existing = bestByManagerId.get(String(managerId));
            if (!existing || membersCount > (existing.membersCount || 0)) {
                bestByManagerId.set(String(managerId), {
                    value: String(managerId),
                    label,
                    email,
                    groupId: group?.id,
                    membersCount,
                });
            }
        }

        return Array.from(bestByManagerId.values()).sort((a, b) =>
            String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' })
        );
    }, [permissionGroupsForManagers, employees]);

    const managerSearchResults = useMemo(() => {
        const term = managerSearchTerm.toLowerCase().trim();
        if (!term) return [];
        return (managerOptions || [])
            .filter((opt) => {
                const label = String(opt.label || '').toLowerCase();
                const email = String(opt.email || '').toLowerCase();
                return label.includes(term) || email.includes(term);
            })
            .slice(0, 10);
    }, [managerSearchTerm, managerOptions]);

    // Keep the search input in sync with the selected manager (so selection is visible)
    useEffect(() => {
        if (!selectedManagerId) {
            setManagerSearchTerm('');
            return;
        }
        const match = (managerOptions || []).find(
            (o) => String(o.value) === String(selectedManagerId)
        );
        if (match?.label) {
            setManagerSearchTerm(match.label);
        }
    }, [selectedManagerId, managerOptions]);

    const selectedManagerOption = useMemo(() => {
        if (!selectedManagerId) return null;
        return (managerOptions || []).find(
            (o) => String(o.value) === String(selectedManagerId)
        ) || null;
    }, [managerOptions, selectedManagerId]);

    const selectedManagerGroup = useMemo(() => {
        if (!selectedManagerId) return null;

        // Prefer the specific group chosen when we deduped manager options (more consistent)
        const preferredGroupId = selectedManagerOption?.groupId;
        if (preferredGroupId != null) {
            const matchById = (permissionGroupsForManagers || []).find(
                (g) => String(g?.id) === String(preferredGroupId)
            );
            if (matchById) return matchById;
        }

        // Fallback: match by manager id
        return (permissionGroupsForManagers || []).find((group) => {
            const managerId =
                typeof group?.manager === 'object' ? group.manager?.id : group?.manager;
            return String(managerId) === String(selectedManagerId);
        }) || null;
    }, [permissionGroupsForManagers, selectedManagerId, selectedManagerOption?.groupId]);

    // Prefer detailed group (with members) when available, mirroring GroupDetail logic
    const selectedManagerGroupWithMembers = useMemo(() => {
        if (!selectedManagerGroup) return null;
        if (
            organization_group_detail &&
            organization_group_detail.id === selectedManagerGroup.id &&
            Array.isArray(organization_group_detail.members)
        ) {
            return organization_group_detail;
        }
        return selectedManagerGroup;
    }, [selectedManagerGroup, organization_group_detail]);

    // Ensure we fetch full group details (including members) when a manager is selected
    useEffect(() => {
        if (!selectedManagerGroup || !selectedManagerGroup.id) return;
        dispatch({
            type: SG_FETCH_ORGANIZATION_GROUP,
            payload: { id: selectedManagerGroup.id },
        });
    }, [dispatch, selectedManagerGroup && selectedManagerGroup.id]);

    const selectedMemberIds = useMemo(() => {
        const groupForMembers = selectedManagerGroupWithMembers;
        if (!groupForMembers || !Array.isArray(groupForMembers.members)) return [];

        return groupForMembers.members
            .map((member) => {
                if (typeof member === 'object' && member !== null) {
                    // Support various member shapes: { id }, { employee_id }, { employeeId }, { employee }, or raw id
                    if (member.id != null) return member.id;
                    if (member.employee_id != null) return member.employee_id;
                    if (member.employeeId != null) return member.employeeId;
                    if (member.employee != null) {
                        // member.employee may be an id or an object with id
                        if (typeof member.employee === 'object' && member.employee !== null) {
                            return member.employee.id ?? null;
                        }
                        return member.employee;
                    }
                    return null;
                }
                return member;
            })
            .filter((id) => id !== null && id !== undefined)
            .map((id) => Number(id));
    }, [selectedManagerGroupWithMembers]);

    const handleCalculate = async () => {
        if (!canCalculate) return;

        setLoading(true);
        setError(null);
        setCalculationResult(null);
        // Reset token usage
        setTokenUsage({
            prompt_tokens: 0,
            completion_tokens: 0,
            total_tokens: 0,
            call_count: 0,
            input_cost: 0,
            output_cost: 0,
            total_cost: 0
        });

        try {
            // Find the selected calculation function
            const selectedOption = calculationOptions.find(option => option.value === selectedCalculation);
            if (!selectedOption) {
                throw new Error('Invalid calculation type selected');
            }

            // Guard: Manager edition requires a manager selection + member ids
            if (selectedCalculation === 'organizationReportManager') {
                if (!selectedManagerId) {
                    throw new Error("Organization Report (Manager edition) requires selecting a Manager.");
                }
                if (!Array.isArray(selectedMemberIds) || selectedMemberIds.length === 0) {
                    throw new Error("Selected manager has 0 loaded members. Please re-select the manager and wait for group members to load, then try again.");
                }
            }

            // Create token update callback with cost calculation
            const handleTokenUpdate = (newTokenUsage) => {
                // Calculate costs using selected model pricing
                const inputCost = (newTokenUsage.prompt_tokens / 1000000) * currentModel.inputCostPerMillion;
                const outputCost = (newTokenUsage.completion_tokens / 1000000) * currentModel.outputCostPerMillion;
                const totalCost = inputCost + outputCost;

                setTokenUsage({
                    ...newTokenUsage,
                    input_cost: inputCost,
                    output_cost: outputCost,
                    total_cost: totalCost
                });
            };

            const memberIdSet =
                selectedMemberIds && selectedMemberIds.length > 0
                    ? new Set(selectedMemberIds.map((id) => Number(id)))
                    : null;

            const extractEmployeeId = (entry) => {
                if (!entry) return null;
                const possible =
                    entry.employee_id ??
                    entry.employeeId ??
                    entry.emp_id ??
                    entry.empId ??
                    entry.employee?.id ??
                    entry.employee ??
                    entry.user_id ??
                    entry.userId ??
                    entry.respondent_id ??
                    entry.respondentId ??
                    null;
                return possible !== null && possible !== undefined ? Number(possible) : null;
            };

            const filterByMembers = (list) => {
                if (!Array.isArray(list) || !memberIdSet) return list || [];
                return list.filter((item) => {
                    const empId = extractEmployeeId(item);
                    return empId !== null && memberIdSet.has(empId);
                });
            };

            let managerData = null;
            if (selectedManagerId) {
                const filteredResponses = filterByMembers(raw_data?.[0]?.responses || []);
                const filteredLast = filterByMembers(raw_data?.[0]?.last || []);
                const managerName = formatManagerName(
                    selectedManagerGroup?.manager,
                    selectedManagerId
                );
                managerData = {
                    manager_id: Number(selectedManagerId),
                    manager_name: managerName,
                    group_id: selectedManagerGroup?.id || null,
                    group_name: selectedManagerGroup?.name || null,
                    member_ids: selectedMemberIds,
                    responses: filteredResponses,
                    last: filteredLast,
                    source: 'manager_group_filter',
                };

                console.log('[ReportV2] Manager filter debug', {
                    selectedManagerId,
                    selectedMemberIdsCount: selectedMemberIds.length,
                    sampleMemberIds: selectedMemberIds.slice(0, 5),
                    rawResponsesCount: Array.isArray(raw_data?.[0]?.responses) ? raw_data[0].responses.length : 0,
                    filteredResponsesCount: Array.isArray(filteredResponses) ? filteredResponses.length : 0
                });
            }

            // Pass the selected anchor parent ID, selected model, and token callback to the calculation function
            const result = await selectedOption.func(
                raw_data,
                core_data,
                debriefSchedules,
                selectedAnchorParent,
                handleTokenUpdate,
                selectedModel,
                selectedAnchors,
                combineAnchors,
                managerData
            );
            setCalculationResult(result);
        } catch (err) {
            console.error('Calculation error:', err);
            setError(err.message || 'An error occurred during calculation');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyToClipboard = async () => {
        if (!calculationResult) return;

        try {
            await navigator.clipboard.writeText(JSON.stringify(calculationResult, null, 2));
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    return (
        <Container>
            <Header>
                <Title>Report Analysis v2</Title>
                <Subtitle>Select a calculation type and anchor parent to generate detailed analysis results</Subtitle>
            </Header>

            <ControlsSection>
                <ControlGroup>
                    <Label htmlFor="calculation-select">Calculation Type</Label>
                    <Select 
                        id="calculation-select"
                        value={selectedCalculation}
                        onChange={(e) => setSelectedCalculation(e.target.value)}
                        disabled={loading}
                    >
                        {calculationOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="anchor-parent-select">Anchor Parent</Label>
                    <Select 
                        id="anchor-parent-select"
                        value={selectedAnchorParent}
                        onChange={(e) => setSelectedAnchorParent(e.target.value)}
                        disabled={loading || anchorParents.length === 0}
                    >
                        {anchorParents.length === 0 ? (
                            <option value="">No anchor parents available</option>
                        ) : (
                            anchorParents.map(parent => (
                                <option key={parent.id} value={parent.id}>
                                    {parent.name || `Anchor Parent ${parent.id}`}
                                </option>
                            ))
                        )}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="debrief-select">Survey Debrief</Label>
                    <Select
                        id="debrief-select"
                        value={selectedDebriefId}
                        onChange={(e) => setSelectedDebriefId(e.target.value)}
                        disabled={loading || debriefOptions.length === 0}
                        style={{ minWidth: 260 }}
                    >
                        <option value="">Latest (default)</option>
                        {debriefOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="manager-search">Manager (optional)</Label>
                    <ManagerSearchRow>
                        <ManagerSearchContainer>
                            <ManagerSearchInput
                                id="manager-search"
                                type="text"
                                placeholder={
                                    allGroupsLoading
                                        ? 'Loading managers...'
                                        : 'Search manager by name or email...'
                                }
                                value={managerSearchTerm}
                                onChange={(e) => {
                                    setManagerSearchTerm(e.target.value);
                                    if (!showManagerDropdown) setShowManagerDropdown(true);
                                }}
                                onFocus={() => {
                                    if (managerSearchTerm.trim()) setShowManagerDropdown(true);
                                }}
                                onBlur={() => {
                                    // Delay hiding so clicks can register
                                    setTimeout(() => setShowManagerDropdown(false), 150);
                                }}
                                disabled={loading || allGroupsLoading}
                                aria-label="Search managers"
                            />
                            {showManagerDropdown && managerSearchTerm.trim() && (
                                <ManagerDropdown>
                                    {managerSearchResults.length > 0 ? (
                                        managerSearchResults.map((opt) => (
                                            <ManagerDropdownItem
                                                key={opt.value}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => {
                                                    setSelectedManagerId(String(opt.value));
                                                    setManagerSearchTerm(opt.label || '');
                                                    setShowManagerDropdown(false);
                                                }}
                                            >
                                                <ManagerDropdownName>{opt.label}</ManagerDropdownName>
                                                {opt.email ? (
                                                    <ManagerDropdownEmail>{opt.email}</ManagerDropdownEmail>
                                                ) : null}
                                            </ManagerDropdownItem>
                                        ))
                                    ) : (
                                        <ManagerNoResults>No matching managers</ManagerNoResults>
                                    )}
                                </ManagerDropdown>
                            )}
                        </ManagerSearchContainer>

                        {selectedManagerId ? (
                            <ManagerClearButton
                                type="button"
                                onClick={() => {
                                    setSelectedManagerId('');
                                    setManagerSearchTerm('');
                                    setShowManagerDropdown(false);
                                }}
                            >
                                Clear
                            </ManagerClearButton>
                        ) : null}
                    </ManagerSearchRow>

                    {!selectedManagerId ? (
                        <ManagerInlineNote>Empty = All managers (no filter)</ManagerInlineNote>
                    ) : null}

                    {allGroupsError ? (
                        <ManagerInlineError>
                            Manager list may be incomplete: {allGroupsError}
                        </ManagerInlineError>
                    ) : null}
                </ControlGroup>

                {/* Anchors multi-select */}
                <ControlGroup>
                    <Label htmlFor="anchors-select">Anchors (optional)</Label>
                    <Select
                        id="anchors-select"
                        multiple
                        value={selectedAnchors.map(String)}
                        onChange={(e) => {
                            const parentObj = anchorParents.find(p => String(p.id) === String(selectedAnchorParent));
                            const children = parentObj?.options || [];
                            const values = Array.from(e.target.selectedOptions).map(o => o.value);
                            const typedIds = values
                                .map(v => children.find(ch => String(ch.id) === String(v))?.id)
                                .filter(v => v !== undefined && v !== null);
                            setSelectedAnchors(typedIds);
                        }}
                        disabled={loading || !selectedAnchorParent}
                        style={{ minHeight: 120 }}
                    >
                        {(anchorParents.find(p => String(p.id) === String(selectedAnchorParent))?.options || []).map(child => (
                            <option key={child.id} value={String(child.id)}>
                                {child.name || `Anchor ${child.id}`}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="combine-anchors">Combine selected anchors</Label>
                    <input
                        id="combine-anchors"
                        type="checkbox"
                        checked={combineAnchors}
                        onChange={(e) => setCombineAnchors(e.target.checked)}
                        disabled={loading || selectedAnchors.length < 2}
                    />
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="model-select">Model</Label>
                    <Select 
                        id="model-select"
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        disabled={loading}
                    >
                        {modelOptions.map(model => (
                            <option key={model.value} value={model.value}>
                                {model.label}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <TriggerButton 
                    onClick={handleCalculate}
                    disabled={!canCalculate || loading}
                >
                    {loading ? 'Calculating...' : 'Run Calculation'}
                </TriggerButton>
            </ControlsSection>

            {(loading || tokenUsage.call_count > 0) && (
                <TokenUsageDisplay>
                    <TokenUsageTitle>
                        🤖 LLM Token Usage {loading && '(Real-time)'}
                    </TokenUsageTitle>
                    <TokenRow>
                        <TokenLabel>API Calls:</TokenLabel>
                        <TokenValue>{tokenUsage.call_count}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Input Tokens:</TokenLabel>
                        <TokenValue>{tokenUsage.prompt_tokens.toLocaleString()}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Output Tokens:</TokenLabel>
                        <TokenValue>{tokenUsage.completion_tokens.toLocaleString()}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Total Tokens:</TokenLabel>
                        <TokenValue>{tokenUsage.total_tokens.toLocaleString()}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Input Cost:</TokenLabel>
                        <CostValue>${tokenUsage.input_cost.toFixed(4)}</CostValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Output Cost:</TokenLabel>
                        <CostValue>${tokenUsage.output_cost.toFixed(4)}</CostValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Total Cost:</TokenLabel>
                        <CostValue>${tokenUsage.total_cost.toFixed(4)}</CostValue>
                    </TokenRow>
                    <PricingNote>
                        {currentModel.label} Pricing: ${currentModel.inputCostPerMillion} per 1M input tokens • ${currentModel.outputCostPerMillion} per 1M output tokens
                    </PricingNote>
                </TokenUsageDisplay>
            )}

            {!canCalculate && (
                <StatusText>
                    Waiting for required data... 
                    {!raw_data && 'Missing raw data. '}
                    {!core_data && 'Missing core data. '}
                    {!selectedAnchorParent && 'Please select an anchor parent.'}
                </StatusText>
            )}

            {error && (
                <ErrorMessage>
                    <strong>Error:</strong> {error}
                </ErrorMessage>
            )}

            {loading && <LoadingSpinner />}

            {calculationResult && (
                <ResultsSection>
                    <ResultsHeader>
                        <ResultsTitle>Calculation Results</ResultsTitle>
                        <CopyButton onClick={handleCopyToClipboard}>
                            {copySuccess ? '✓ Copied!' : 'Copy JSON'}
                        </CopyButton>
                    </ResultsHeader>
                    <JsonContainer>
                        <pre>{JSON.stringify(calculationResult, null, 2)}</pre>
                    </JsonContainer>
                </ResultsSection>
            )}

            {!calculationResult && !loading && !error && canCalculate && (
                <EmptyState>
                    Select a calculation type and anchor parent, then click "Run Calculation" to see results
                </EmptyState>
            )}
        </Container>
    );
};

export default ReportV2;